import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Table } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Sample MLB trending players data from the spreadsheet
const mlbTrendingData = [
  { player: 'Aaron Judge', trendScore: 361.8, position: 'OF', team: 'NYY' },
  { player: 'Shohei Ohtani', trendScore: 327.3, position: 'DH/P', team: 'LAD' },
  { player: 'Juan Soto', trendScore: 276.6, position: 'OF', team: 'NYY' },
  { player: 'José Ramírez', trendScore: 268.35, position: '3B', team: 'CLE' },
  { player: 'Bobby Witt Jr.', trendScore: 260.1, position: 'SS', team: 'KC' },
  { player: 'Kyle Tucker', trendScore: 260.1, position: 'OF', team: 'HOU' },
  { player: 'Gunnar Henderson', trendScore: 253.65, position: 'SS', team: 'BAL' },
  { player: 'Francisco Lindor', trendScore: 248.7, position: 'SS', team: 'NYM' },
  { player: 'Elly De La Cruz', trendScore: 214.5, position: 'SS', team: 'CIN' },
  { player: 'Corbin Carroll', trendScore: 198, position: 'OF', team: 'ARI' },
  { player: 'Tarik Skubal', trendScore: 59, position: 'P', team: 'DET' },
  { player: 'Zack Wheeler', trendScore: 53.8, position: 'P', team: 'PHI' },
  { player: 'Framber Valdez', trendScore: 53.6, position: 'P', team: 'HOU' },
  { player: 'Dylan Cease', trendScore: 53, position: 'P', team: 'SD' },
  { player: 'Paul Skenes', trendScore: 50.8, position: 'P', team: 'PIT' },
  { player: 'Max Fried', trendScore: 49.2, position: 'P', team: 'ATL' },
  { player: 'Logan Webb', trendScore: 48.8, position: 'P', team: 'SF' },
  { player: 'Corbin Burnes', trendScore: 48.6, position: 'P', team: 'BAL' },
  { player: 'Aaron Nola', trendScore: 47.6, position: 'P', team: 'PHI' },
  { player: 'Logan Gilbert', trendScore: 44.4, position: 'P', team: 'SEA' }
];

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('trending');
  const [playersData, setPlayersData] = useState([]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setPlayersData(mlbTrendingData);
  }, []);

  // Redirect to profile setup if user doesn't have a profile
  useEffect(() => {
    if (currentUser) {
      // The Navigation component will handle profile loading and redirection if needed
    }
  }, [currentUser, navigate]);

  const getScoreColor = (score) => {
    if (score > 250) return '#ff6b6b';
    if (score > 100) return '#4ecdc4';
    if (score > 50) return '#45b7d1';
    return '#96ceb4';
  };

  const renderPlayerCard = (player, index) => (
    <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-4">
      <Card className="h-100 shadow-sm" style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
        <Card.Body className="text-center">
          <div 
            className="mb-3 mx-auto rounded-circle d-flex align-items-center justify-content-center"
            style={{ 
              width: '60px', 
              height: '60px', 
              backgroundColor: getScoreColor(player.trendScore),
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            #{index + 1}
          </div>
          <h6 className="text-light mb-2">{player.player}</h6>
          <p className="text-muted mb-1">{player.position} • {player.team}</p>
          <h5 style={{ color: getScoreColor(player.trendScore) }}>
            {player.trendScore}
          </h5>
          <small className="text-muted">Trend Score</small>
        </Card.Body>
      </Card>
    </Col>
  );

  return (
    <div style={{ backgroundColor: '#0f1419', minHeight: '100vh' }}>
      {/* Tab Navigation */}
      <div style={{ backgroundColor: '#1a1d29', borderBottom: '1px solid #2d3343' }}>
        <Container>
          <div className="d-flex py-3 dashboard-tabs">
            <button
              className={`btn me-3 ${activeTab === 'trending' ? 'btn-primary' : 'btn-outline-light'}`}
              onClick={() => setActiveTab('trending')}
            >
              🔥 Trending Players
            </button>
            <button
              className={`btn me-3 ${activeTab === 'season' ? 'btn-primary' : 'btn-outline-light'}`}
              onClick={() => setActiveTab('season')}
            >
              📊 Season Stats
            </button>
            <button
              className={`btn ${activeTab === 'trends' ? 'btn-primary' : 'btn-outline-light'}`}
              onClick={() => setActiveTab('trends')}
            >
              📈 Market Trends
            </button>
          </div>
        </Container>
      </div>

      <Container fluid className="py-4">
        {activeTab === 'trending' && (
          <>
            <Row className="mb-4">
              <Col>
                <h2 className="text-light mb-1">🔥 MLB Trending Players 2025</h2>
                <p className="text-muted">Real-time trending scores for the hottest players in baseball</p>
              </Col>
            </Row>
            
            <Row className="mb-4">
              <Col md={3}>
                <Card style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
                  <Card.Body className="text-center">
                    <h3 style={{ color: '#ff6b6b' }}>361.8</h3>
                    <p className="text-light mb-0">Highest Score</p>
                    <small className="text-muted">Aaron Judge</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
                  <Card.Body className="text-center">
                    <h3 style={{ color: '#4ecdc4' }}>20</h3>
                    <p className="text-light mb-0">Total Players</p>
                    <small className="text-muted">Trending Now</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
                  <Card.Body className="text-center">
                    <h3 style={{ color: '#45b7d1' }}>186.9</h3>
                    <p className="text-light mb-0">Average Score</p>
                    <small className="text-muted">Top 10 Players</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
                  <Card.Body className="text-center">
                    <h3 style={{ color: '#96ceb4' }}>11</h3>
                    <p className="text-light mb-0">Pitchers</p>
                    <small className="text-muted">vs 9 Position Players</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              {playersData.map((player, index) => renderPlayerCard(player, index))}
            </Row>
          </>
        )}

        {activeTab === 'season' && (
          <Row>
            <Col>
              <Card style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
                <Card.Header style={{ backgroundColor: '#2d3343', color: '#fff' }}>
                  <h5 className="mb-0">📊 Season Statistics</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive variant="dark">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Team</th>
                        <th>Position</th>
                        <th>Trend Score</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playersData.slice(0, 10).map((player, index) => (
                        <tr key={index}>
                          <td>
                            <span 
                              className="badge rounded-pill"
                              style={{ backgroundColor: getScoreColor(player.trendScore) }}
                            >
                              #{index + 1}
                            </span>
                          </td>
                          <td className="fw-bold">{player.player}</td>
                          <td>{player.team}</td>
                          <td>{player.position}</td>
                          <td style={{ color: getScoreColor(player.trendScore) }}>
                            {player.trendScore}
                          </td>
                          <td>
                            <span className="badge bg-success">Active</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {activeTab === 'trends' && (
          <Row>
            <Col>
              <Card style={{ backgroundColor: '#1a1d29', border: '1px solid #2d3343' }}>
                <Card.Header style={{ backgroundColor: '#2d3343', color: '#fff' }}>
                  <h5 className="mb-0">📈 Market Trends & Analysis</h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-light">
                    This section would contain detailed market trends, price analysis, and trading insights 
                    from various marketplaces including eBay trends and seller summaries.
                  </p>
                  <div className="text-center py-5">
                    <h4 className="text-muted">Coming Soon</h4>
                    <p className="text-muted">Advanced analytics and market trends will be available here.</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
} 